import { DATE_FORMAT, Link, Tag } from '@dev-spendesk/grapes';
import { useState } from 'react';
import { Trans } from 'react-i18next';

import { useCardLimitsIncreaseStatus } from 'modules/cards/hooks/useCardLimitsIncreaseStatus';
import { useGetCardProductRegulatoryLimits } from 'modules/cards/hooks/useGetCardProductRegulatoryLimits';
import { ErrorState } from 'src/core/common/components/ErrorState';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { AnalyticEventName, track } from 'src/core/utils/analytics';

import { CardLimitsIncreaseModalTrigger } from './components/CardLimitsIncreaseModalTrigger';
import { CardLimitsScaModal } from './components/CardLimitsScaModal';
import { CardLimitsTable } from './components/CardLimitsTable';
import { CardLimitsTableLoader } from './components/CardLimitsTableLoader';

export const CardLimits = () => {
  const { t, localeFormat } = useTranslation('global');

  const companyId = useCompanyId();
  const physicalCardLimitsQuery = useGetCardProductRegulatoryLimits({
    cardProduct: 'physical',
    companyId,
  });
  const virtualCardLimitsQuery = useGetCardProductRegulatoryLimits({
    cardProduct: 'subscription',
    companyId,
  });

  const {
    isLoading: isCardLimitsIncreaseStatusLoading,
    isLimitsApproved,
    canRequestIncrease,
    data: cardLimitsIncreaseStatusData,
    isRequestCardLimitsIncreaseEnabled,
  } = useCardLimitsIncreaseStatus();

  const [increaseJustification, setIncreaseJustification] = useState('');
  const [isScaModalOpen, setIsScaModalOpen] = useState(false);

  const handleOpenScaModal = () => {
    track(AnalyticEventName.SETTINGS_CARD_LIMITS_INCREASE_SUBMIT);
    setIsScaModalOpen(true);
    track(AnalyticEventName.SETTINGS_CARD_LIMITS_INCREASE_SCA_START);
  };

  const isLoading =
    physicalCardLimitsQuery.isLoading ||
    virtualCardLimitsQuery.isLoading ||
    isCardLimitsIncreaseStatusLoading;

  const isLimitsError =
    physicalCardLimitsQuery.isError || virtualCardLimitsQuery.isError;
  const hasLimits = physicalCardLimitsQuery.data && virtualCardLimitsQuery.data;

  if (isLimitsError) {
    return (
      <ErrorState
        className="ml-48 justify-start"
        title={t('generalSettings.loadErrorTitle')}
        description={t('generalSettings.loadErrorDescription')}
      />
    );
  }

  if (isScaModalOpen) {
    return (
      <CardLimitsScaModal
        companyId={companyId}
        increaseJustification={increaseJustification}
        isOpen={isScaModalOpen}
        onClose={() => {
          setIsScaModalOpen(false);
        }}
      />
    );
  }

  return (
    <section
      aria-labelledby="cardLimits"
      className="GeneralSettingsPage__section"
    >
      <div className="flex gap-8">
        <div className="flex flex-col">
          <div className="flex items-center gap-8">
            <h1 id="cardLimits" className="text-content-primary heading-l">
              {t('generalSettings.cardLimits.title')}
            </h1>
            {isRequestCardLimitsIncreaseEnabled && (
              <Tag variant="pink" className="h-[20px]">
                {t('misc.new')}
              </Tag>
            )}
          </div>
          <p className="text-content-secondary-bg-primary body-m mt-8">
            <Trans
              i18nKey="generalSettings.cardLimits.description"
              components={{
                a: (
                  <Link
                    href="https://helpcenter.spendesk.com/en/articles/8764665-card-limits-explained"
                    target="_blank"
                    rel="noreferrer"
                  >
                    -
                  </Link>
                ),
              }}
            />
            {isLimitsApproved && (
              <>
                <br />
                <br />
                <span className="italic">
                  <Trans
                    i18nKey="generalSettings.cardLimits.increase.success"
                    values={{
                      requesterName:
                        cardLimitsIncreaseStatusData?.requesterName,
                      requestDate: cardLimitsIncreaseStatusData?.requestDate
                        ? localeFormat(
                            new Date(cardLimitsIncreaseStatusData.requestDate),
                            DATE_FORMAT.SHORT,
                          )
                        : '',
                    }}
                  />
                </span>
                <br />
              </>
            )}
          </p>
        </div>

        <div>
          {!isLoading &&
            hasLimits &&
            canRequestIncrease &&
            isRequestCardLimitsIncreaseEnabled && (
              <CardLimitsIncreaseModalTrigger
                increaseJustification={increaseJustification}
                setIncreaseJustification={setIncreaseJustification}
                onSubmit={handleOpenScaModal}
              />
            )}
        </div>
      </div>
      {isLoading && <CardLimitsTableLoader />}
      {!isLoading && hasLimits && (
        <CardLimitsTable
          physicalCardLimits={physicalCardLimitsQuery.data}
          virtualCardLimits={virtualCardLimitsQuery.data}
          className="mt-24"
        />
      )}
    </section>
  );
};
